import {useEffect, useState, useCallback} from 'react';

import throttle from 'util/throttle';

interface ScrollPosition {
  scrollId: string; // ID of the item currently in view
  auto: boolean; // true if selected automatically by our scroll detection, false if selected by jumpToItem
}

/**
 * This hook provides two-way sync with a document's scroll position.
 *
 * - If we scroll in the document, update the selected item to the top-most item currently in view.
 * - If we update the selected item programmatically, scroll it into view in the document.
 *
 * @param scrollIds - a sequential list (top-down) of the IDs of all significant items.
 * @param getElementByScrollId - maps an item's ID to its corresponding HTMLElement.
 * @param container - the viewport (i.e., scrollable container) that contains all of the significant items.
 * @param customFindElementInViewport - a custom function to find the element in view.
 * @param customScrollIntoView - a custom function to scroll an element into view.
 * @param listenDuringCapturingPhase - whether to listen for scroll events during the capturing phase.
 *
 * @returns {scrollPosition, jumpToItem} - where:
 *   - scrollPosition holds both the scrollId of the item currently in view and whether it was selected
 *      automatically by our scroll detection or manually by jumpToItem.
 *   - jumpToItem updates the selected ID and scrolls the item into view (e.g. in response to a user action)
 */
export default function useDocumentScrollState(
  scrollIds: Array<string>,
  getElementByScrollId: (scrollId: string) => HTMLElement | null,
  container: HTMLElement | null,
  customScrollIntoView?: (node: HTMLElement) => void,
  customFindElementInViewport?: (
    container: HTMLElement,
    getElementByScrollId: (cid: string) => HTMLElement | null,
    scrollIds: Array<string>
  ) => string | null,
  listenDuringCapturingPhase?: boolean
): {scrollPosition: ScrollPosition | null; jumpToScrollId: (scrollId: string) => void} {
  const [position, setPosition] = useState<ScrollPosition | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const findElementInViewport = customFindElementInViewport ?? findFirstItemInViewport;
  // disabling the exhaustive-deps lint rule because we only want to update when the scroll ids change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledCallback = useCallback(
    throttle(() => {
      if (!container) {
        return;
      }

      // If we jump to an item near the end of the document, where an earlier item will be the first visible
      // in the viewport, keep the former as the current position.
      if (position && !position.auto) {
        const node = getElementByScrollId(position.scrollId);
        if (node && isInViewport(container, node)) {
          return;
        }
      }

      const firstItemInViewport = findElementInViewport(container, getElementByScrollId, scrollIds);
      if (firstItemInViewport == null) {
        return;
      }

      setPosition({scrollId: firstItemInViewport, auto: true});
    }, 200),
    [JSON.stringify(scrollIds)]
  );

  // Register a scroll handler on the document that updates our selected state to reflect the top-most visible
  // item.
  useEffect(() => {
    if (!container) {
      return () => {};
    }
    container.addEventListener('scroll', throttledCallback, {capture: listenDuringCapturingPhase ?? false});
    return () => {
      throttledCallback.cancel();
      container.removeEventListener('scroll', throttledCallback, {
        capture: listenDuringCapturingPhase ?? false,
      });
    };
    // Use JSON.stringify for deep comparison of the scrollIds array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, position, getElementByScrollId, throttledCallback, JSON.stringify(scrollIds)]);

  // If the caller manually updates the selected state via `jumpToCid`, scroll the node into view.
  useEffect(() => {
    if (position == null || position.auto) {
      return;
    }
    const node = getElementByScrollId(position.scrollId);
    if (!node) {
      return;
    }
    if (customScrollIntoView) {
      customScrollIntoView(node);
    } else {
      node.scrollIntoView();
      // Avoid odd browser bug where the entire page is scrolled if height is small.
      window.scrollTo(0, 0);
    }
  }, [position, getElementByScrollId, customScrollIntoView, container]);

  function jumpToScrollId(scrollId: string): void {
    setSelectedId(scrollId);
    setPosition({scrollId, auto: false});
  }
  let scrollPosition = position;
  if (selectedId) {
    const node = getElementByScrollId(selectedId);
    if (node && container && isInViewport(container, node)) {
      scrollPosition = {scrollId: selectedId, auto: true};
    }
  }
  return {scrollPosition, jumpToScrollId};
}

/**
 * Return the scrollId of the first item that is visible in the container.
 */
function findFirstItemInViewport(
  container: HTMLElement,
  getElementByScrollId: (cid: string) => HTMLElement | null,
  scrollIds: Array<string>
): string | null {
  for (const id of scrollIds) {
    const el = getElementByScrollId(id);
    if (!el) {
      continue;
    }
    if (isInViewport(container, el)) {
      return id;
    }
  }
  return null;
}

function isInViewport(container: HTMLElement, el: HTMLElement): boolean {
  const containerBox = container.getBoundingClientRect();
  const box = el.getBoundingClientRect();
  return box.top >= containerBox.top && box.bottom <= containerBox.bottom;
}
